import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  DIRECTED_CAST_APPEARANCE_MAX_LENGTH,
  DIRECTED_CAST_ART_STYLE_MAX_LENGTH,
  DIRECTED_CAST_KEEP_ITEM_MAX_LENGTH,
  DIRECTED_CAST_KEEP_MAX,
  DIRECTED_CAST_SUBJECT_MAX_LENGTH,
  type DirectedCastDescription,
  type DirectedCastEntry,
} from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { castDescriptionMessages as messages } from '../messages/castDescriptionMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DescriptionDraft {
  artStyle: string;
  subject: string;
  appearance: string;
  keep: string;
}

/** The fields of the editor for a description (or empty ones). Pure. */
export function descriptionDraftOf(description: DirectedCastDescription | null): DescriptionDraft {
  return {
    artStyle: description?.artStyle ?? '',
    subject: description?.subject ?? '',
    appearance: description?.appearance ?? '',
    keep: (description?.doNotChange ?? []).join(', '),
  };
}

/**
 * The description that the editor sends: trimmed fields, the "keep" items split on commas, at most
 * `DIRECTED_CAST_KEEP_MAX`. Null when every field is empty (the description goes). Pure.
 */
export function descriptionOfDraft(draft: DescriptionDraft): DirectedCastDescription | null {
  const doNotChange = draft.keep
    .split(',')
    .map((item) => item.trim().slice(0, DIRECTED_CAST_KEEP_ITEM_MAX_LENGTH))
    .filter((item) => item !== '')
    .slice(0, DIRECTED_CAST_KEEP_MAX);
  const description = {
    artStyle: draft.artStyle.trim(),
    subject: draft.subject.trim(),
    appearance: draft.appearance.trim(),
    doNotChange,
  };
  return description.artStyle === '' && description.subject === '' && description.appearance === ''
    ? null
    : description;
}

interface CastDescriptionProps {
  entry: DirectedCastEntry;
  readOnly: boolean;
  onSave: (description: DirectedCastDescription | null) => void;
}

/**
 * The description of one cast picture under the picture (fix 2026-10-18): "Reading the picture…"
 * while the AI reads it, then what the director sees (the art style, who or what, the look, what
 * stays the same). The artist corrects it in place; the director then uses the artist's words.
 */
const CastDescriptionComponent: React.FC<CastDescriptionProps> = ({ entry, readOnly, onSave }) => {
  const intl = useIntl();
  const [draft, setDraft] = useState<DescriptionDraft | null>(null);
  const { description, descriptionStatus } = entry;

  if (draft !== null) {
    const field = (key: keyof DescriptionDraft, label: string, maxLength: number, placeholder?: string) => (
      <div className={shell.castDescriptionField} key={key}>
        <span className={styles.fieldLabel} aria-hidden>
          {label}
        </span>
        {key === 'appearance' ? (
          <textarea
            className={styles.input}
            rows={3}
            aria-label={label}
            value={draft[key]}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
          />
        ) : (
          <input
            className={styles.input}
            aria-label={label}
            value={draft[key]}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
          />
        )}
      </div>
    );
    return (
      <div className={shell.castDescription} data-testid="cast-description-editor">
        {field(
          'artStyle',
          intl.formatMessage(messages.artStyle),
          DIRECTED_CAST_ART_STYLE_MAX_LENGTH,
          intl.formatMessage(messages.artStyleHint)
        )}
        {field('subject', intl.formatMessage(messages.subject), DIRECTED_CAST_SUBJECT_MAX_LENGTH)}
        {field(
          'appearance',
          intl.formatMessage(messages.appearance),
          DIRECTED_CAST_APPEARANCE_MAX_LENGTH,
          intl.formatMessage(messages.appearanceHint)
        )}
        {field(
          'keep',
          intl.formatMessage(messages.keep),
          DIRECTED_CAST_KEEP_MAX * (DIRECTED_CAST_KEEP_ITEM_MAX_LENGTH + 2)
        )}
        <div className={shell.row}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              onSave(descriptionOfDraft(draft));
              setDraft(null);
            }}
          >
            {intl.formatMessage(messages.save)}
          </button>
          <button type="button" className={styles.linkButton} onClick={() => setDraft(null)}>
            {intl.formatMessage(messages.cancel)}
          </button>
        </div>
      </div>
    );
  }

  if (descriptionStatus === 'reading') {
    return (
      <p className={shell.hint} role="status" data-testid="cast-reading">
        <span className={shell.spinner} aria-hidden />
        {intl.formatMessage(messages.reading)}
      </p>
    );
  }

  const startEdit = () => setDraft(descriptionDraftOf(description));

  if (description === null) {
    return (
      <div className={shell.castDescription}>
        <p className={clsx(shell.hint, descriptionStatus === 'failed' && shell.error)}>
          {intl.formatMessage(descriptionStatus === 'failed' ? messages.failed : messages.none)}
        </p>
        {!readOnly && (
          <button type="button" className={styles.linkButton} onClick={startEdit}>
            {intl.formatMessage(messages.describe)}
          </button>
        )}
      </div>
    );
  }

  const look = [description.subject, description.appearance].filter((part) => part !== '').join(': ');
  return (
    <div className={shell.castDescription} data-testid="cast-description">
      <span className={shell.cardEyebrow}>
        {intl.formatMessage(entry.descriptionSource === 'artist' ? messages.byYou : messages.title)}
      </span>
      {description.artStyle !== '' && <p className={shell.cardText}>{description.artStyle}</p>}
      {look !== '' && <p className={shell.hint}>{look}</p>}
      {description.doNotChange.length > 0 && (
        <p className={shell.hint}>
          {intl.formatMessage(messages.keepLine, { items: description.doNotChange.join(', ') })}
        </p>
      )}
      {!readOnly && (
        <button type="button" className={styles.linkButton} onClick={startEdit}>
          {intl.formatMessage(messages.edit)}
        </button>
      )}
    </div>
  );
};

export const CastDescription = TypedMemo(CastDescriptionComponent);
